import { QrCodeIcon } from "@heroicons/react/16/solid";
import { ColumnDef } from "@tanstack/react-table";
import { LineClampedTooltip } from "@/core/components/line-clamped-tooltip";
import { Stack } from "@/core/components/ui/stack/stack";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";
import { getCustomerNameOrPhone } from "@/customer/utils";
import { IReferral } from "@/referrals/referral";

interface ContentProps {
  nameOrPhone: string;
  attributedTo?: string | null;
  isDeleted: boolean;
}

const Content = ({ nameOrPhone, attributedTo, isDeleted }: ContentProps) => (
  <Stack justify="center" gap="none" className="h-full w-full">
    <LineClampedTooltip>{nameOrPhone}</LineClampedTooltip>
    {attributedTo && (
      <Tooltip>
        <TooltipTrigger asChild>
          <Stack direction="row" gap="xs">
            <QrCodeIcon
              className={cn(
                "h-4 w-4 min-w-4 text-tertiary-text",
                isDeleted && "text-quaternary-text",
              )}
            />
            <Typography
              variant="bodyW2"
              textColor={isDeleted ? "quaternary" : "tertiary"}
              className="line-clamp-1"
            >
              {attributedTo}
            </Typography>
          </Stack>
        </TooltipTrigger>
        <TooltipContent maxWidth="xl">
          <b>{attributedTo}</b> signed up the advocate that sent this referral
        </TooltipContent>
      </Tooltip>
    )}
  </Stack>
);

const AdvocateCol = (): ColumnDef<IReferral> => ({
  header: "Advocate",
  accessorFn: (row) => getCustomerNameOrPhone(row.advocate),
  cell: ({ row, getValue }) => {
    const nameOrPhone = getValue() as string;
    const attributedTo =
      row.original.advocate.signup_source?.display_identification;

    return (
      <Content
        nameOrPhone={nameOrPhone}
        attributedTo={attributedTo}
        isDeleted={!!row.original.advocate.signup_source?.discarded_at}
      />
    );
  },
});

export default AdvocateCol;
